import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLogto } from '@logto/react';
import {
  Upload,
  FolderOpen,
  Check,
  ChevronRight,
  Loader2,
  Sparkles,
  ArrowRight,
  BarChart3,
  Calendar,
  DollarSign,
  Target,
  Settings,
  Play,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import Papa, { ParseResult } from 'papaparse';
import { storageService } from '../../services/storageService';
import { configService } from '../../services/configService';
import {
  MMMConfig,
  MediaChannel,
  ControlVar,
  DEFAULT_MMM_CONFIG,
  DataFrequency,
} from '../../types/mmmConfig';

// Conversation step types
type ConversationStep =
  | 'welcome'
  | 'data-source'
  | 'upload-data'
  | 'select-saved-data'
  | 'dependent-variable'
  | 'date-column'
  | 'media-spend'
  | 'performance-metrics'
  | 'control-variables'
  | 'confirm-ignored'
  | 'validating'
  | 'ready-to-run'
  | 'running'
  | 'error';

interface Message {
  id: string;
  type: 'assistant' | 'user' | 'system';
  content: React.ReactNode;
  timestamp: Date;
}

interface SpendToMetricMapping {
  spendColumn: string;
  metricColumn: string | null;
}

interface ModelBuilderProps {
  onAnalysisComplete: (jobId: string) => void;
  onBack: () => void;
}

export const ModelBuilder: React.FC<ModelBuilderProps> = ({
  onAnalysisComplete,
  onBack,
}) => {
  const logto = useLogto();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Conversation state
  const [currentStep, setCurrentStep] = useState<ConversationStep>('welcome');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  // Data state
  const [dataFileId, setDataFileId] = useState<string>('');
  const [dataColumns, setDataColumns] = useState<string[]>([]);
  const [, setDataFileName] = useState<string>('');
  const [, setDataRowCount] = useState<number>(0);

  // Configuration state
  const [selectedDepVar, setSelectedDepVar] = useState<string>('');
  const [selectedDateVar, setSelectedDateVar] = useState<string>('');
  const [selectedSpendColumns, setSelectedSpendColumns] = useState<string[]>([]);
  const [spendToMetricMappings, setSpendToMetricMappings] = useState<SpendToMetricMapping[]>([]);
  const [selectedControlColumns, setSelectedControlColumns] = useState<string[]>([]);
  const [ignoredColumns, setIgnoredColumns] = useState<string[]>([]);
  const [detectedFrequency, setDetectedFrequency] = useState<DataFrequency>('W');

  // Analysis state
  const [, setValidationErrors] = useState<string[]>([]);
  const [, setValidationWarnings] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [, setIsValidating] = useState(false);
  const [, setIsRunning] = useState(false);
  const [runProgress, setRunProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Generate unique message ID
  const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Add message helper
  const addMessage = useCallback((type: Message['type'], content: React.ReactNode) => {
    const newMessage: Message = {
      id: generateId(),
      type,
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  // Simulate typing effect
  const typeMessage = useCallback(
    async (type: Message['type'], content: React.ReactNode, delay = 600) => {
      setIsTyping(true);
      await new Promise((resolve) => setTimeout(resolve, delay));
      setIsTyping(false);
      addMessage(type, content);
    },
    [addMessage]
  );

  // Initialize welcome message
  useEffect(() => {
    const initConversation = async () => {
      await typeMessage(
        'assistant',
        <div>
          <p className="text-lg font-medium text-slate-800 mb-2">
            Welcome! I'm here to help you build your Marketing Mix Model.
          </p>
          <p className="text-slate-600">
            I'll guide you through each step to configure your model. Let's start by getting your data ready.
          </p>
        </div>,
        800
      );
      setCurrentStep('data-source');
    };
    initConversation();
  }, []);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setIsUploading(true);
    addMessage('user', `Uploading ${file.name}...`);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result as string;
      Papa.parse(content, {
        header: false,
        skipEmptyLines: true,
        complete: async (results: ParseResult<string[]>) => {
          try {
            const data = results.data as string[][];
            if (data.length === 0) {
              throw new Error('CSV file is empty');
            }

            const columnNames = data[0];
            const numRows = data.length - 1;

            // Save to backend
            const saveResult = await storageService.saveDataset(
              logto,
              file,
              file.name.replace('.csv', '') || 'dataset',
              `Uploaded via Model Builder on ${new Date().toLocaleString()}`
            );

            if (saveResult.success && saveResult.dataset_id) {
              setDataFileId(saveResult.dataset_id);
              setDataColumns(columnNames);
              setDataFileName(file.name);
              setDataRowCount(numRows);

              setIsUploading(false);
              await typeMessage(
                'assistant',
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Data uploaded successfully!</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 mt-3">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">File</span>
                        <p className="font-medium text-slate-800 truncate">{file.name}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Rows</span>
                        <p className="font-medium text-slate-800">{numRows.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Columns</span>
                        <p className="font-medium text-slate-800">{columnNames.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
              setCurrentStep('dependent-variable');
            } else {
              throw new Error(saveResult.message || 'Failed to save dataset');
            }
          } catch (error) {
            setIsUploading(false);
            const errorMsg = error instanceof Error ? error.message : 'Failed to process file';
            addMessage('system', <span className="text-rose-600">{errorMsg}</span>);
          }
        },
        error: (error: Error) => {
          setIsUploading(false);
          addMessage('system', <span className="text-rose-600">{error.message}</span>);
        },
      });
    };
    reader.readAsText(file);
  };

  // Handle saved dataset selection
  const handleSelectSavedDataset = async (datasetId: string, datasetName: string, metadata: any) => {
    addMessage('user', `Selected saved dataset: ${datasetName}`);

    setDataFileId(datasetId);
    setDataFileName(datasetName);

    // Fetch actual columns from the backend
    let columns: string[] = metadata.columns || [];
    let rows = metadata.rows || 0;

    try {
      const columnsResult = await configService.getDataColumns(logto, datasetId);
      if (columnsResult.success && columnsResult.columns) {
        columns = columnsResult.columns;
        rows = columnsResult.sample_rows || rows;
      }
    } catch (e) {
      console.error('Failed to fetch columns:', e);
    }

    setDataColumns(columns);
    setDataRowCount(rows);

    await typeMessage(
      'assistant',
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-emerald-600">
          <Check className="w-5 h-5" />
          <span className="font-medium">Dataset loaded successfully!</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 mt-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Dataset</span>
              <p className="font-medium text-slate-800 truncate">{datasetName}</p>
            </div>
            <div>
              <span className="text-slate-500">Rows</span>
              <p className="font-medium text-slate-800">{rows.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-slate-500">Columns</span>
              <p className="font-medium text-slate-800">{columns.length}</p>
            </div>
          </div>
        </div>
      </div>
    );
    setCurrentStep('dependent-variable');
  };

  // Handle dependent variable selection
  const handleDepVarSelect = async (column: string) => {
    setSelectedDepVar(column);
    addMessage('user', `Selected "${column}" as the target metric`);

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">Great choice!</span>
        </div>
        <p className="text-slate-600">
          Your model will learn what drives <span className="font-semibold text-slate-800">{column}</span>.
        </p>
      </div>
    );
    setCurrentStep('date-column');
  };

  // Handle date column selection
  const handleDateVarSelect = async (column: string) => {
    setSelectedDateVar(column);
    addMessage('user', `Selected "${column}" as the date column`);

    // Detect frequency
    let frequencyLabel = 'Weekly';
    if (dataFileId) {
      try {
        const result = await configService.detectFrequency(logto, dataFileId, column);
        if (result.success && result.frequency) {
          setDetectedFrequency(result.frequency as DataFrequency);
          frequencyLabel = result.frequency === 'D' ? 'Daily' : result.frequency === 'W' ? 'Weekly' : 'Monthly';
        }
      } catch (e) {
        console.error('Failed to detect frequency:', e);
      }
    }

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">Date column configured!</span>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 mt-2 text-sm">
          <span className="text-slate-500">Detected frequency: </span>
          <span className="font-medium text-slate-800">{frequencyLabel}</span>
        </div>
      </div>
    );
    setCurrentStep('media-spend');
  };

  // Handle media spend selection
  const handleSpendColumnsSelect = async (columns: string[]) => {
    setSelectedSpendColumns(columns);
    // Initialize mappings
    setSpendToMetricMappings(columns.map((col) => ({ spendColumn: col, metricColumn: null })));

    addMessage(
      'user',
      <div>
        <span>Selected {columns.length} media spend column(s):</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {columns.map((col) => (
            <span key={col} className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-sm">
              {col}
            </span>
          ))}
        </div>
      </div>
    );

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">{columns.length} media channels identified!</span>
        </div>
        <p className="text-slate-600">
          Now let's map each spend column to its corresponding performance metric (impressions, clicks, GRPs, etc.).
          This helps the model understand the relationship between what you spend and what you get.
        </p>
      </div>
    );
    setCurrentStep('performance-metrics');
  };

  // Handle metric mapping
  const handleMetricMappingComplete = async (mappings: SpendToMetricMapping[]) => {
    setSpendToMetricMappings(mappings);

    const mappedCount = mappings.filter((m) => m.metricColumn).length;
    addMessage(
      'user',
      `Mapped ${mappedCount} of ${mappings.length} spend columns to performance metrics`
    );

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">Performance metrics configured!</span>
        </div>
        <p className="text-slate-600">
          Now let's identify your control variables. These are factors that influence your target but aren't
          part of your media spend - things like organic traffic, economic indicators, or competitor activity.
        </p>
      </div>
    );
    setCurrentStep('control-variables');
  };

  // Handle control variables selection
  const handleControlColumnsSelect = async (columns: string[]) => {
    setSelectedControlColumns(columns);

    // Calculate ignored columns
    const usedColumns = new Set([
      selectedDepVar,
      selectedDateVar,
      ...selectedSpendColumns,
      ...spendToMetricMappings.filter((m) => m.metricColumn).map((m) => m.metricColumn!),
      ...columns,
    ]);
    const ignored = dataColumns.filter((col) => !usedColumns.has(col));
    setIgnoredColumns(ignored);

    addMessage(
      'user',
      columns.length > 0 ? (
        <div>
          <span>Selected {columns.length} control variable(s):</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {columns.map((col) => (
              <span key={col} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-sm">
                {col}
              </span>
            ))}
          </div>
        </div>
      ) : (
        'No control variables selected'
      )
    );

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">Control variables set!</span>
        </div>
        {ignored.length > 0 && (
          <p className="text-slate-600">
            I found {ignored.length} column(s) that won't be used in the model. Let me show you which ones
            so you can confirm or go back to add them.
          </p>
        )}
      </div>
    );
    setCurrentStep('confirm-ignored');
  };

  // Handle ignored columns confirmation
  const handleConfirmIgnored = async () => {
    addMessage('user', 'Confirmed - proceeding with validation');

    await typeMessage(
      'assistant',
      <div>
        <div className="flex items-center gap-2 text-violet-600 mb-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">Validating your configuration...</span>
        </div>
        <p className="text-slate-600">
          I'm checking your data and configuration for any issues.
        </p>
      </div>
    );
    setCurrentStep('validating');
    runValidation();
  };

  // Run validation
  const runValidation = async () => {
    setIsValidating(true);
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation checks
    if (!selectedDepVar) errors.push('No dependent variable selected');
    if (!selectedDateVar) errors.push('No date column selected');
    if (selectedSpendColumns.length === 0) errors.push('No media spend columns selected');

    // Check for missing values, etc. (simplified)
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    setIsValidating(false);

    if (errors.length === 0) {
      await typeMessage(
        'assistant',
        <div>
          <div className="flex items-center gap-2 text-emerald-600 mb-2">
            <Check className="w-5 h-5" />
            <span className="font-medium">Validation passed!</span>
          </div>
          <p className="text-slate-600">
            Your configuration looks good. You're ready to run your Marketing Mix Model!
          </p>
          {warnings.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm font-medium">
                {warnings.length} warning(s) detected
              </p>
              <ul className="mt-1 text-sm text-amber-700">
                {warnings.map((w, i) => (
                  <li key={i}>- {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
      setCurrentStep('ready-to-run');
    } else {
      await typeMessage(
        'assistant',
        <div>
          <div className="flex items-center gap-2 text-rose-600 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Validation found issues</span>
          </div>
          <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
            <ul className="text-sm text-rose-700">
              {errors.map((e, i) => (
                <li key={i}>- {e}</li>
              ))}
            </ul>
          </div>
        </div>
      );
      setCurrentStep('error');
    }
  };

  // Run the model
  const handleRunModel = async () => {
    setIsRunning(true);
    setRunProgress(0);
    addMessage('user', 'Starting model training...');

    // Build configuration
    const config: MMMConfig = {
      ...DEFAULT_MMM_CONFIG,
      dep_var: selectedDepVar,
      date_var: selectedDateVar,
      frequency: detectedFrequency,
      media_channels: selectedSpendColumns.map((col) => {
        const mapping = spendToMetricMappings.find((m) => m.spendColumn === col);
        return {
          name: col.replace(/_spend$/i, '').replace(/_/g, ' '),
          column: col,
          variable_column: mapping?.metricColumn || null,
          adstock: 'geometric',
          saturation: 'hill',
        } as MediaChannel;
      }),
      control_vars: selectedControlColumns.map((col) => ({
        name: col,
        column: col,
      })) as ControlVar[],
    };

    try {
      setCurrentStep('running');

      await typeMessage(
        'assistant',
        <div>
          <div className="flex items-center gap-2 text-violet-600 mb-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-medium">Training your model...</span>
          </div>
          <p className="text-slate-600">
            This typically takes 1-3 minutes depending on your data size and configuration.
          </p>
        </div>
      );

      const result = await configService.runAnalysis(logto, config, dataFileId);

      if (result.success && result.job_id) {
        setJobId(result.job_id);
        pollJobStatus(result.job_id);
      } else {
        throw new Error(result.message || 'Failed to start analysis');
      }
    } catch (error) {
      setIsRunning(false);
      const errorMsg = error instanceof Error ? error.message : 'Failed to run analysis';
      setErrorMessage(errorMsg);
      addMessage('system', <span className="text-rose-600">{errorMsg}</span>);
      setCurrentStep('error');
    }
  };

  // Poll job status
  const pollJobStatus = useCallback(
    async (id: string) => {
      try {
        const response = await configService.checkAnalysisStatus(logto, id);

        if (response.status === 'completed') {
          setRunProgress(100);
          setIsRunning(false);
          await typeMessage(
            'assistant',
            <div>
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Model training complete!</span>
              </div>
              <p className="text-slate-600">
                Your Marketing Mix Model is ready. Click below to view your results.
              </p>
            </div>
          );
          setTimeout(() => onAnalysisComplete(id), 1500);
        } else if (response.status === 'failed' || response.status === 'error') {
          setIsRunning(false);
          setErrorMessage(response.error || 'Analysis failed');
          setCurrentStep('error');
        } else {
          setRunProgress(response.progress || 0);
          setTimeout(() => pollJobStatus(id), 2000);
        }
      } catch (error) {
        setIsRunning(false);
        setErrorMessage('Failed to check analysis status');
        setCurrentStep('error');
      }
    },
    [logto, onAnalysisComplete, typeMessage]
  );

  // Get available columns for each step
  const getAvailableColumns = (exclude: string[] = []) => {
    const excludeSet = new Set(exclude);
    return dataColumns.filter((col) => !excludeSet.has(col));
  };

  // Render current step UI
  const renderCurrentStepUI = () => {
    switch (currentStep) {
      case 'data-source':
        return (
          <DataSourceSelector
            onUploadClick={() => {
              setCurrentStep('upload-data');
              addMessage('user', 'I want to upload new data');
            }}
            onSavedClick={() => {
              setCurrentStep('select-saved-data');
              addMessage('user', 'I want to use previously saved data');
            }}
          />
        );

      case 'upload-data':
        return (
          <DataUploader
            isUploading={isUploading}
            onFileSelect={handleFileUpload}
            fileInputRef={fileInputRef}
          />
        );

      case 'select-saved-data':
        return (
          <SavedDatasetSelector
            logto={logto}
            onSelect={handleSelectSavedDataset}
          />
        );

      case 'dependent-variable':
        return (
          <ColumnSelector
            title="Which metric are you trying to influence?"
            description="This is your target variable - typically revenue, sales, conversions, or another KPI you want to optimize."
            columns={dataColumns}
            selected={selectedDepVar}
            onSelect={handleDepVarSelect}
            icon={<Target className="w-5 h-5" />}
            examples={['revenue', 'sales', 'conversions', 'leads', 'signups']}
          />
        );

      case 'date-column':
        return (
          <ColumnSelector
            title="Which column contains your dates?"
            description="This helps the model understand the time dimension of your data."
            columns={getAvailableColumns([selectedDepVar])}
            selected={selectedDateVar}
            onSelect={handleDateVarSelect}
            icon={<Calendar className="w-5 h-5" />}
            examples={['date', 'week', 'month', 'period']}
          />
        );

      case 'media-spend':
        return (
          <MultiColumnSelector
            title="Which columns represent your media spend?"
            description="Select all columns that contain marketing spend data for different channels (e.g., Facebook spend, Google spend, TV spend)."
            columns={getAvailableColumns([selectedDepVar, selectedDateVar])}
            selected={selectedSpendColumns}
            onConfirm={handleSpendColumnsSelect}
            icon={<DollarSign className="w-5 h-5" />}
          />
        );

      case 'performance-metrics':
        return (
          <MetricMappingEditor
            mappings={spendToMetricMappings}
            availableColumns={getAvailableColumns([
              selectedDepVar,
              selectedDateVar,
              ...selectedSpendColumns,
            ])}
            onConfirm={handleMetricMappingComplete}
          />
        );

      case 'control-variables':
        return (
          <MultiColumnSelector
            title="Which columns are control variables?"
            description="These are factors that affect your target but aren't media spend. Examples include organic traffic, Google Trends data, economic indicators, or competitor metrics."
            columns={getAvailableColumns([
              selectedDepVar,
              selectedDateVar,
              ...selectedSpendColumns,
              ...spendToMetricMappings.filter((m) => m.metricColumn).map((m) => m.metricColumn!),
            ])}
            selected={selectedControlColumns}
            onConfirm={handleControlColumnsSelect}
            icon={<Settings className="w-5 h-5" />}
            allowEmpty
          />
        );

      case 'confirm-ignored':
        return (
          <IgnoredColumnsConfirmation
            ignoredColumns={ignoredColumns}
            onConfirm={handleConfirmIgnored}
            onGoBack={() => setCurrentStep('control-variables')}
          />
        );

      case 'validating':
        return (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-violet-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Validating configuration...</p>
            </div>
          </div>
        );

      case 'ready-to-run':
        return (
          <ReadyToRunPanel
            config={{
              depVar: selectedDepVar,
              dateVar: selectedDateVar,
              mediaChannels: selectedSpendColumns.length,
              controlVars: selectedControlColumns.length,
              frequency: detectedFrequency,
            }}
            onRun={handleRunModel}
          />
        );

      case 'running':
        return (
          <RunningProgressPanel
            progress={runProgress}
            jobId={jobId}
          />
        );

      case 'error':
        return (
          <ErrorPanel
            message={errorMessage || 'An error occurred'}
            onRetry={() => {
              setErrorMessage(null);
              setCurrentStep('data-source');
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Model Builder</h1>
            <p className="text-sm text-slate-500">Conversational MMM Configuration</p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Conversation Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-gradient-to-b from-slate-50 to-white">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-slate-200 bg-white px-6 py-4">
        {/* Key forces re-mount on step change, triggering animation */}
        {/* Pulse animation draws attention to new questions */}
        <div
          key={currentStep}
          className={
            // Only apply pulse to question steps, not loading/running states
            !['welcome', 'validating', 'running'].includes(currentStep)
              ? 'question-pulse'
              : ''
          }
        >
          {renderCurrentStepUI()}
        </div>
      </div>
    </div>
  );
};

// === Sub-components ===

const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
  const isAssistant = message.type === 'assistant';
  const isSystem = message.type === 'system';

  return (
    <div className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 ${
          isAssistant
            ? 'bg-white border border-slate-200 rounded-2xl rounded-bl-md shadow-sm'
            : isSystem
            ? 'bg-amber-50 border border-amber-200 rounded-2xl'
            : 'bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl rounded-br-md shadow-lg shadow-violet-500/20'
        }`}
      >
        <div className={isAssistant || isSystem ? 'text-slate-800' : 'text-white'}>
          {message.content}
        </div>
      </div>
    </div>
  );
};

const DataSourceSelector: React.FC<{
  onUploadClick: () => void;
  onSavedClick: () => void;
}> = ({ onUploadClick, onSavedClick }) => (
  <div className="space-y-3">
    <p className="text-sm text-slate-600 mb-4">How would you like to get started?</p>
    <div className="grid grid-cols-2 gap-4">
      <button
        onClick={onUploadClick}
        className="group p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-violet-400 hover:shadow-lg transition-all text-left"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
          <Upload className="w-6 h-6 text-violet-600" />
        </div>
        <h3 className="font-semibold text-slate-900 mb-1">Upload New Data</h3>
        <p className="text-sm text-slate-500">Import a CSV file with your marketing data</p>
      </button>

      <button
        onClick={onSavedClick}
        className="group p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-violet-400 hover:shadow-lg transition-all text-left"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
          <FolderOpen className="w-6 h-6 text-teal-600" />
        </div>
        <h3 className="font-semibold text-slate-900 mb-1">Use Saved Data</h3>
        <p className="text-sm text-slate-500">Select from previously uploaded datasets</p>
      </button>
    </div>
  </div>
);

const DataUploader: React.FC<{
  isUploading: boolean;
  onFileSelect: (file: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}> = ({ isUploading, onFileSelect, fileInputRef }) => (
  <div className="space-y-4">
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
        isUploading ? 'border-violet-300 bg-violet-50' : 'border-slate-300 hover:border-violet-400 bg-white'
      }`}
    >
      {isUploading ? (
        <div className="flex flex-col items-center">
          <Loader2 className="w-10 h-10 text-violet-500 animate-spin mb-3" />
          <p className="text-slate-600">Processing your file...</p>
        </div>
      ) : (
        <>
          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-700 font-medium mb-1">Drop your CSV file here</p>
          <p className="text-slate-500 text-sm mb-4">or click to browse</p>
          <label className="inline-block">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])}
              className="hidden"
              disabled={isUploading}
            />
            <span className="btn btn-primary cursor-pointer">Select File</span>
          </label>
        </>
      )}
    </div>
    <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
      <p className="font-medium text-slate-700 mb-2">Data requirements:</p>
      <ul className="space-y-1 text-slate-500">
        <li>- First row must contain column headers</li>
        <li>- Include a date column (YYYY-MM-DD format)</li>
        <li>- Include your target metric (revenue, sales, etc.)</li>
        <li>- Include media spend columns for each channel</li>
      </ul>
    </div>
  </div>
);

const SavedDatasetSelector: React.FC<{
  logto: any;
  onSelect: (id: string, name: string, metadata: any) => void;
}> = ({ logto, onSelect }) => {
  const [datasets, setDatasets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDatasets = async () => {
      try {
        // listDatasets returns an array directly
        const result = await storageService.listDatasets(logto);
        if (Array.isArray(result)) {
          setDatasets(result);
        }
      } catch (e) {
        console.error('Failed to load datasets:', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadDatasets();
  }, [logto]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (datasets.length === 0) {
    return (
      <div className="text-center py-8">
        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600">No saved datasets found</p>
        <p className="text-sm text-slate-500 mt-1">Upload a new file to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {datasets.map((ds) => (
        <button
          key={ds.id}
          onClick={() => onSelect(ds.id, ds.name, ds.metadata || {})}
          className="w-full p-4 bg-white border border-slate-200 rounded-xl hover:border-violet-400 hover:shadow-md transition-all text-left flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
            <FolderOpen className="w-5 h-5 text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{ds.name}</p>
            <p className="text-sm text-slate-500">
              {ds.metadata?.rows?.toLocaleString() || '?'} rows ·{' '}
              {ds.metadata?.columns?.length || '?'} columns
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </button>
      ))}
    </div>
  );
};

const ColumnSelector: React.FC<{
  title: string;
  description: string;
  columns: string[];
  selected: string;
  onSelect: (column: string) => void;
  icon: React.ReactNode;
  examples?: string[];
}> = ({ title, description, columns, selected, onSelect, icon, examples }) => (
  <div className="space-y-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center flex-shrink-0 text-violet-600">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
        {examples && (
          <p className="text-xs text-slate-400 mt-1">
            Common names: {examples.join(', ')}
          </p>
        )}
      </div>
    </div>

    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
      {columns.map((col) => (
        <button
          key={col}
          onClick={() => onSelect(col)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            selected === col
              ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
              : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300 hover:bg-violet-50'
          }`}
        >
          {col}
        </button>
      ))}
    </div>
  </div>
);

const MultiColumnSelector: React.FC<{
  title: string;
  description: string;
  columns: string[];
  selected: string[];
  onConfirm: (columns: string[]) => void;
  icon: React.ReactNode;
  allowEmpty?: boolean;
}> = ({ title, description, columns, selected: initialSelected, onConfirm, icon, allowEmpty }) => {
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const toggleColumn = (col: string) => {
    setSelected((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center flex-shrink-0 text-violet-600">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
        {columns.map((col) => (
          <button
            key={col}
            onClick={() => toggleColumn(col)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              selected.includes(col)
                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300 hover:bg-violet-50'
            }`}
          >
            {col}
            {selected.includes(col) && <Check className="w-4 h-4 ml-1.5 inline" />}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <span className="text-sm text-slate-500">
          {selected.length} selected
        </span>
        <button
          onClick={() => onConfirm(selected)}
          disabled={!allowEmpty && selected.length === 0}
          className="btn btn-primary"
        >
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

const MetricMappingEditor: React.FC<{
  mappings: SpendToMetricMapping[];
  availableColumns: string[];
  onConfirm: (mappings: SpendToMetricMapping[]) => void;
}> = ({ mappings: initialMappings, availableColumns, onConfirm }) => {
  const [mappings, setMappings] = useState<SpendToMetricMapping[]>(initialMappings);

  const updateMapping = (spendColumn: string, metricColumn: string | null) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.spendColumn === spendColumn ? { ...m, metricColumn } : m
      )
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-slate-900">Map spend to performance metrics</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          For each spend column, select the corresponding delivery metric (impressions, clicks, etc.) or "None" if not available.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Media Spend
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Performance Metric
              </th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((mapping, idx) => (
              <tr key={mapping.spendColumn} className={idx > 0 ? 'border-t border-slate-100' : ''}>
                <td className="px-4 py-3">
                  <span className="font-medium text-slate-800">{mapping.spendColumn}</span>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={mapping.metricColumn || ''}
                    onChange={(e) =>
                      updateMapping(mapping.spendColumn, e.target.value || null)
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    <option value="">None</option>
                    {availableColumns.map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <button onClick={() => onConfirm(mappings)} className="btn btn-primary">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

const IgnoredColumnsConfirmation: React.FC<{
  ignoredColumns: string[];
  onConfirm: () => void;
  onGoBack: () => void;
}> = ({ ignoredColumns, onConfirm, onGoBack }) => (
  <div className="space-y-4">
    <div>
      <h3 className="font-semibold text-slate-900">Confirm unused columns</h3>
      <p className="text-sm text-slate-500 mt-0.5">
        The following {ignoredColumns.length} column(s) will not be included in the model:
      </p>
    </div>

    {ignoredColumns.length > 0 ? (
      <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-xl max-h-32 overflow-y-auto">
        {ignoredColumns.map((col) => (
          <span
            key={col}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600"
          >
            {col}
          </span>
        ))}
      </div>
    ) : (
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
        <p className="text-emerald-700 text-sm">
          All columns in your dataset have been assigned a role.
        </p>
      </div>
    )}

    <div className="flex items-center gap-3 pt-2">
      <button onClick={onGoBack} className="btn btn-secondary">
        Go Back
      </button>
      <button onClick={onConfirm} className="btn btn-primary flex-1">
        Confirm & Validate <Check className="w-4 h-4 ml-1" />
      </button>
    </div>
  </div>
);

const ReadyToRunPanel: React.FC<{
  config: {
    depVar: string;
    dateVar: string;
    mediaChannels: number;
    controlVars: number;
    frequency: string;
  };
  onRun: () => void;
}> = ({ config, onRun }) => (
  <div className="space-y-4">
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
      <div className="flex items-center gap-2 text-emerald-700 mb-3">
        <Check className="w-5 h-5" />
        <span className="font-semibold">Ready to Build Your Model</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-emerald-600 text-xs uppercase tracking-wide">Target</span>
          <p className="font-semibold text-emerald-900">{config.depVar}</p>
        </div>
        <div>
          <span className="text-emerald-600 text-xs uppercase tracking-wide">Date Column</span>
          <p className="font-semibold text-emerald-900">{config.dateVar}</p>
        </div>
        <div>
          <span className="text-emerald-600 text-xs uppercase tracking-wide">Media Channels</span>
          <p className="font-semibold text-emerald-900">{config.mediaChannels}</p>
        </div>
        <div>
          <span className="text-emerald-600 text-xs uppercase tracking-wide">Control Variables</span>
          <p className="font-semibold text-emerald-900">{config.controlVars}</p>
        </div>
      </div>
    </div>

    <button
      onClick={onRun}
      className="w-full btn btn-primary btn-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
    >
      <Play className="w-5 h-5 mr-2" />
      Run the Model
    </button>
  </div>
);

const RunningProgressPanel: React.FC<{
  progress: number;
  jobId: string | null;
}> = ({ progress, jobId }) => (
  <div className="space-y-4">
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
        <div>
          <h3 className="font-semibold text-violet-900">Training Your Model</h3>
          <p className="text-sm text-violet-600">
            Optimizing hyperparameters and fitting the model...
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-violet-700">Progress</span>
          <span className="font-semibold text-violet-900">{progress}%</span>
        </div>
        <div className="w-full bg-violet-100 rounded-full h-3 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 transition-all duration-500 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
          </div>
        </div>
      </div>

      {jobId && (
        <div className="mt-4 pt-4 border-t border-violet-200">
          <span className="text-xs text-violet-500">Job ID: </span>
          <code className="text-xs font-mono text-violet-700 bg-violet-100 px-2 py-0.5 rounded">
            {jobId}
          </code>
        </div>
      )}
    </div>
  </div>
);

const ErrorPanel: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => (
  <div className="space-y-4">
    <div className="bg-gradient-to-br from-rose-50 to-red-50 border border-rose-200 rounded-xl p-5">
      <div className="flex items-center gap-2 text-rose-700 mb-2">
        <AlertCircle className="w-5 h-5" />
        <span className="font-semibold">Something went wrong</span>
      </div>
      <p className="text-rose-600 text-sm">{message}</p>
    </div>

    <button onClick={onRetry} className="btn btn-secondary w-full">
      <RefreshCw className="w-4 h-4 mr-2" />
      Start Over
    </button>
  </div>
);

export default ModelBuilder;
